import React, { useEffect, useState } from "react";
import { Box, Typography, Tabs, Tab, Paper, Button } from "@mui/material";

const SummerWorkshop2027Application = ({ defaultTab = 0 }) => {
  const initialTab = defaultTab >= 0 && defaultTab < 3 ? defaultTab : 0;

  const [tabValue, setTabValue] = useState(initialTab);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    document.title = "Summer Residency Application 2027 - MS Digital Hub";
  }, []);

  return (
    <Box
      sx={{
        width: "auto",
        padding: "0px 20px",
        margin: "0px auto",
        padding: { xs: "20px", sm: "30px", md: "1px 80px" },
        border: "none",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
      }}
    >
      {/* Header */}
      <Box
        my={4}
        sx={{
          gap: 4,
          alignItems: "center",
        }}
      >
        <Box>
          <Box>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                textAlign: "center",
                marginBottom: "30px",
              }}
            >
              Apply to Our Digital Humanities Summer Residency Program 2027
            </Typography>

            {/* Application Button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: "15px",
              }}
            >
              <Button
                variant="contained"
                disabled
                sx={{
                  backgroundColor: "#FFD700",
                  color: "#111111",
                  padding: "12px 20px",
                  fontWeight: "bold",

                  "&:hover": {
                    backgroundColor: "#FFB800",
                  },

                  "&.Mui-disabled": {
                    backgroundColor: "#FFD700",
                    color: "#555555",
                    opacity: 0.7,
                  },
                }}
              >
                Applications Open November 1, 2026
              </Button>
            </Box>
          </Box>

          {/* Program Details */}
          <Box
            sx={{
              display: "flex",
              alignItems: {
                xs: "center",
                sm: "center",
                md: "flex-start",
                lg: "flex-start",
              },
              justifyContent: "space-between",
              padding: "20px",
              gap: { xs: "30px", sm: "30px" },
              flexDirection: {
                xs: "column",
                sm: "column",
                md: "row",
                lg: "row",
              },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                flex: 1,
                textAlign: "center",
              }}
            >
              <strong>
                Dates
                <br />
              </strong>
              Monday, June 14, 2027 - Friday, June 25, 2027
            </Typography>

            <Typography
              variant="body1"
              sx={{
                flex: 1,
                textAlign: "center",
              }}
            >
              <strong>
                Location
                <br />
              </strong>
              The University of Southern Mississippi - Hattiesburg campus
            </Typography>

            <Typography
              variant="body1"
              sx={{
                flex: 1,
                textAlign: "center",
              }}
            >
              <strong>
                Deadline
                <br />
              </strong>
              Application closes: March 15, 2027 at 11:59 PM (CT)
              <br />
              *Note: Late applications will not be accepted.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Navigation Tabs */}
      <Paper
        square
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          aria-label="Summer Residency 2027 application navigation"
          sx={{
            flexWrap: "wrap sm:flex-nowrap",
          }}
        >
          <Tab label="About" sx={{ fontWeight: "bold" }} />

          <Tab label="Qualifications" sx={{ fontWeight: "bold" }} />

          <Tab label="Application" sx={{ fontWeight: "bold" }} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box my={4}>
        {/* ABOUT */}
        {tabValue === 0 && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
              About
            </Typography>

            <Typography variant="body1" sx={{ lineHeight: "1.8" }} paragraph>
              High school teachers at all Mississippi schools are invited to
              apply for a two-week summer residency hosted by The University of
              Southern Mississippi&apos;s Digital Humanities Hub. Participants
              will learn the basics of digitization and the digital humanities
              from experts affiliated with the Center for Digital Humanities at
              Southern Miss and discover opportunities to incorporate digital
              storytelling into their lesson plans and classrooms.
            </Typography>

            <Typography variant="body1" sx={{ lineHeight: "1.8" }} paragraph>
              Digital Humanities is an evolving field that uses computers to
              explore scholarly questions in the humanities (English, history,
              philosophy, and similar subjects). Digital Humanists use a wide
              range of tools, such as mapping, data visualization, text
              analysis, digital exhibits, and digital storytelling, to engage
              students and preserve local history and culture.
            </Typography>

            <Typography variant="body1" sx={{ lineHeight: "1.8" }} paragraph>
              The two-week residential course for{" "}
              <strong>Mississippi teachers only</strong> will take place from
              Monday, June 14, 2027, through Friday, June 25, 2027. Applications
              open November 1, 2026, and are due by 11:59 PM (CT) on March 15,
              2027. Participation is limited to ten teachers.
            </Typography>

            <Typography variant="body1" sx={{ lineHeight: "1.8" }} paragraph>
              All participants will be provided with housing, most meals, travel
              reimbursement, and small stipends. In addition to learning digital
              tools and methods, teachers enrolled in the summer residency will
              explore the cultural and historical sites of Hattiesburg and the
              surrounding communities.
            </Typography>
          </Box>
        )}

        {/* QUALIFICATIONS */}
        {tabValue === 1 && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
              Qualifications
            </Typography>

            <Typography variant="body1" sx={{ lineHeight: "1.8" }} paragraph>
              Applicants must be interested in digital humanities and currently
              employed at a high school in Mississippi. Applicants must be
              either U.S. citizens, U.S. nationals, or permanent residents of
              the U.S.
            </Typography>

            <Typography variant="body1" sx={{ lineHeight: "1.8" }} paragraph>
              If selected, participants cannot have other obligations—such as
              being enrolled in another fellowship, attending online courses, or
              working a part- or full-time job—during the workshop. Workshop
              residents are expected to participate in-person on the campus of
              The University of Southern Mississippi and attend all sessions and
              group activities. There will not be a remote option for this
              workshop.
            </Typography>
          </Box>
        )}

        {/* APPLICATION */}
        {tabValue === 2 && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
              Applications
            </Typography>

            <Typography variant="body1" sx={{ lineHeight: "1.8" }} paragraph>
              Applications open November 1, 2026, and are due by 11:59 PM (CT)
              on March 15, 2027.
            </Typography>

            <Typography variant="body1" sx={{ lineHeight: "1.8" }} paragraph>
              Contact Jamie O&apos;Quinn, Public Programs and Outreach Liaison (
              <a
                href="mailto:jamie.oquinn@usm.edu"
                style={{
                  color: "inherit",
                }}
              >
                jamie.oquinn@usm.edu
              </a>
              ), with any questions about the workshop or the application
              process.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SummerWorkshop2027Application;
